/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import styles from "./reportPanel.module.css";

const cx = (...parts) => parts.filter(Boolean).join(" ");
const dash = (v) => (v === undefined || v === null || v === "" ? "—" : v);

function getAuthToken() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}")?.token;
  } catch {
    return null;
  }
}

function authHeader() {
  const token = getAuthToken();
  if (token) axios.defaults.headers.common["Authorization"] = token;
}

// Best-guess severity when the API doesn't send one explicitly.
// If your endpoint returns `latestFlagSeverity` ("stop" | "warn"), that wins.
function severityOf(p) {
  if (p.latestFlagSeverity) return p.latestFlagSeverity;
  if (!p.latestFlagType) return "";
  return /overdue|stop|exclu/i.test(p.latestFlagType) ? "stop" : "warn";
}

function painTrend(p) {
  const now = p.pain_level;
  const base = p.baselinePainLevel;
  if (now === undefined || now === null) return null;
  if (base === undefined || base === null) return { dir: null, label: `${now}` };
  return { dir: now > base ? "up" : now < base ? "down" : "flat", label: `${base} → ${now}` };
}

function recordLine(t) {
  let text = dash(t.event_type);
  if (t.action_taken) text += ` — ${t.action_taken}`;
  if (/^panel-report-(?:action|close|note)$/.test(String(t.event_type || "")) && t.raw_payload) {
    try {
      const payload = typeof t.raw_payload === "string" ? JSON.parse(t.raw_payload) : t.raw_payload;
      if (payload?.reason) text += ` — ${payload.reason}`;
    } catch (e) {
      // Keep the normal timeline text if an old payload cannot be parsed.
    }
  }
  if (t.actor) text += ` (${t.actor})`;
  return text;
}

const FILTERS = [
  { key: "exc", label: "Exceptions first" },
  { key: "all", label: "Everyone" },
  { key: "measure", label: "Measure due" },
  { key: "paused", label: "Paused" },
];

const STATES = [
  { value: "", label: "All states" },
  { value: "TX", label: "Texas" },
  { value: "GA", label: "Georgia" },
  { value: "NC", label: "North Carolina" },
  { value: "AZ", label: "Arizona" },
];

// ── Detail (expanded row) ────────────────────────────────────────────────
// `d` is whatever comes back from GET .../beneficiary/:ref. All fields are
// optional so the card layout still renders, empty, if the API returns
// nothing or a partial record.
function Detail({ p, d, onPainDecision, painDecisionSaving, onPanelAction, panelActionSaving, onAuditExport, auditExporting }) {
  const sev = severityOf(p);
  const [revisitDate, setRevisitDate] = useState("");
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [fullHistory, setFullHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  // These map to PanelReportModel.getBeneficiaryDetail():
  //   alignment       -> raw access_beneficiary_state row (SELECT *)
  //   condition       -> single self_user_info row (cpg_id/cpg_label/cpg_value/
  //                       final_cpg_classification/final_pain_subtype/pain_level)
  //   outcomeMeasures -> ARRAY of questionnaire_targets rows (one per instrument)
  //   timeline        -> merged events + outreach rows, newest first
  //   careTeam        -> single care_team_clinicians row, or null
  const alignment = d?.alignment || {};
  const condition = d?.condition || {};
  const outcomeRows = d?.outcomeMeasures || [];
  const timeline = d?.timeline || [];
  const careTeam = d?.careTeam || null;
  const painEscalation = d?.painEscalation || (p.hasOpenPainEscalation ? {
    id: p.painEscalationId,
    reference_pain: p.painReferencePain,
    new_pain: p.painNewPain,
    created_at: p.painEscalationDate,
  } : null);
  const painDecision = painEscalation?.decision || null;
  const painDecisionLabel = painDecision?.decision === "resume_changed_plan"
    ? "Resume with a modified plan"
    : painDecision?.decision === "keep_holding"
      ? "Hold and revisit"
      : painDecision?.decision === "be_seen_in_person"
        ? "Refer out for an in-person visit"
        : null;

  const loadFullHistory = async (force = false) => {
    if (fullHistory && !force) return;
    authHeader();
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${p.beneficiaryId}/history`);
      if (resp.data.success) setFullHistory(resp.data.data || []);
      else setHistoryError(resp.data.message || "Failed to load full history.");
    } catch (err) {
      setHistoryError(err.response?.data?.message || "Failed to load full history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleFullHistory = async () => {
    if (showFullHistory) {
      setShowFullHistory(false);
      return;
    }
    setShowFullHistory(true);
    await loadFullHistory();
  };

  const requestPanelAction = async (action, promptText) => {
    const entered = window.prompt(promptText);
    if (entered === null) return;
    const reason = entered.trim();
    if (!reason) {
      window.alert("A reason is required so the action can be written to the record.");
      return;
    }
    const saved = await onPanelAction(action, reason);
    if (saved && showFullHistory) await loadFullHistory(true);
  };

  return (
    <>
      {sev === "stop" && (
        <div className={cx(styles.excbar, styles.excbarStop)}>
          <b>{dash(p.latestFlagType)}</b>
          Care is stopped and does not restart without a recorded decision from
          you. The beneficiary has been told you are looking at it.
        </div>
      )}
      {sev === "warn" && (
        <div className={styles.excbar}>
          <b>{dash(p.latestFlagType)}</b>
          Care continues. This is for your attention rather than your
          permission.
        </div>
      )}

      <div className={styles.dgrid}>
        <div className={styles.dcard}>
          <h5>Alignment and state</h5>
          <div className={styles.dl}>
            <div>Licensure state</div>
            <div>{dash(p.state)}</div>
            <div>Alignment date</div>
            <div>{dash(alignment.alignment_date)}</div>
            <div>Day of period</div>
            <div>{p.dayOfPeriod != null ? `${p.dayOfPeriod} of 365` : "—"}</div>
            <div>Reporting window opens</div>
            <div>{dash(alignment.next_reporting_window_open ?? p.next_reporting_window_open)}</div>
            <div>Reporting window closes</div>
            <div>{dash(alignment.next_reporting_window_close)}</div>
          </div>
        </div>

        <div className={styles.dcard}>
          <h5>Condition and classification</h5>
          <div className={styles.dl}>
            <div>Guideline</div>
            <div>{dash(condition.cpg_label ?? condition.final_cpg_classification)}</div>
            <div>Classification</div>
            <div>{dash(condition.cpg_value ?? condition.cpg_id)}</div>
            <div>Pain subtype</div>
            <div>{dash(condition.final_pain_subtype)}</div>
          </div>
        </div>

        <div className={styles.dcard}>
          <h5>Outcome measures</h5>
          <div className={styles.dl}>
            <div>Next due</div>
            <div>{dash(p.next_reporting_window_open)}</div>
            <div>Pain now</div>
            <div>{p.pain_level != null ? `${p.pain_level} of 10` : "—"}</div>
          </div>
          {outcomeRows.length ? (
            outcomeRows.map((o, i) => (
              <div
                key={i}
                className={styles.dl}
                style={{ marginTop: i === 0 ? ".6rem" : ".3rem", borderTop: "1px solid var(--gray100, #eee)", paddingTop: ".4rem" }}
              >
                <div>Instrument</div>
                <div>{dash(o.instrument_label)}</div>
                <div>Baseline</div>
                <div>{dash(o.baseline_value)}</div>
                <div>Target</div>
                <div>{dash(o.target_value)}</div>
                <div>Scale</div>
                <div>
                  {o.scale_min != null && o.scale_max != null ? `${o.scale_min}–${o.scale_max}` : "—"}
                </div>
              </div>
            ))
          ) : (
            <div style={{ marginTop: ".6rem", fontSize: ".9rem", color: "var(--gray500)" }}>
              No outcome instruments on record.
            </div>
          )}
        </div>

        {painEscalation && (
          <div className={cx(styles.dcard, styles.dcardFull)}>
            <h5>Pain increase requiring therapist decision</h5>
            <div className={styles.dl}>
              <div>Occurrence</div>
              <div>Second pain increase within 30 days</div>
              <div>Reference pain</div>
              <div>{painEscalation.reference_pain != null ? `${painEscalation.reference_pain} of 10` : "—"}</div>
              <div>New pain</div>
              <div>{painEscalation.new_pain != null ? `${painEscalation.new_pain} of 10` : "—"}</div>
              <div>Recorded</div>
              <div>{dash(painEscalation.created_at)}</div>
              <div>Beneficiary note</div>
              <div>{dash(painEscalation.beneficiary_note)}</div>
              <div>Current therapist decision</div>
              <div>{painDecisionLabel || "Decision required"}</div>
              {painDecision?.revisitDate && (
                <>
                  <div>Revisit date</div>
                  <div>{painDecision.revisitDate}</div>
                </>
              )}
            </div>
          </div>
        )}

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Recent record</h5>
          <div className={styles.timeline}>
            {timeline.length ? (
              timeline.map((t, i) => (
                <div className={styles.tl} key={i}>
                  <div className={styles.d}>{dash(t.created_at)}</div>
                  <div>{recordLine(t)}</div>
                </div>
              ))
            ) : (
              <div className={styles.tl}>
                <div className={styles.d}>—</div>
                <div>No recent activity on record.</div>
              </div>
            )}
          </div>
        </div>

        {showFullHistory && (
          <div className={cx(styles.dcard, styles.dcardFull)}>
            <h5>Full history</h5>
            <div className={styles.timeline}>
              {historyLoading ? (
                <div className={styles.tl}>
                  <div className={styles.d}>—</div>
                  <div>Loading full history…</div>
                </div>
              ) : historyError ? (
                <div className={styles.tl}>
                  <div className={styles.d}>—</div>
                  <div>{historyError}</div>
                </div>
              ) : fullHistory?.length ? (
                fullHistory.map((t, i) => (
                  <div className={styles.tl} key={i}>
                    <div className={styles.d}>{dash(t.created_at)}</div>
                    <div>{recordLine(t)}</div>
                  </div>
                ))
              ) : (
                <div className={styles.tl}>
                  <div className={styles.d}>—</div>
                  <div>No history on record.</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={cx(styles.dcard, styles.dcardFull)}>
          <h5>Care team</h5>
          <div style={{ fontSize: ".92rem" }}>
            {careTeam ? (
              <>
                {dash(careTeam.clinician_name ?? careTeam.name)}
                {careTeam.clinic_name ? `, ${careTeam.clinic_name}` : ""}
                {careTeam.status ? ` — ${careTeam.status}` : ""}
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
      </div>

      <div className={styles.acts}>
        {painEscalation ? (
          <>
            <button
              className={cx(styles.btn, styles.btnPrimary)}
              onClick={() => onPainDecision("resume_changed_plan", revisitDate)}
              disabled={painDecisionSaving}
            >
              Resume with a modified plan
            </button>
            <input
              type="date"
              aria-label="Revisit date"
              value={revisitDate}
              onChange={(e) => setRevisitDate(e.target.value)}
              disabled={painDecisionSaving}
            />
            <button
              className={styles.btn}
              onClick={() => onPainDecision("keep_holding", revisitDate)}
              disabled={painDecisionSaving || !revisitDate}
            >
              Hold with revisit date
            </button>
            <button
              className={styles.btn}
              onClick={() => onPainDecision("be_seen_in_person")}
              disabled={painDecisionSaving}
            >
              Refer out for an in-person visit
            </button>
          </>
        ) : sev === "stop" ? (
          <>
            <button
              className={cx(styles.btn, styles.btnPrimary)}
              onClick={() => requestPanelAction("resume_modified_plan", "Reason for resuming with a modified plan:")}
              disabled={panelActionSaving}
            >
              Resume with a modified plan
            </button>
            <button
              className={styles.btn}
              onClick={() => requestPanelAction("hold_recheck", "Reason for holding care and re-checking:")}
              disabled={panelActionSaving}
            >
              Hold and re-check
            </button>
            <button
              className={styles.btn}
              onClick={() => requestPanelAction("refer_in_person", "Reason for referring out for in-person assessment:")}
              disabled={panelActionSaving}
            >
              Refer out for in-person assessment
            </button>
            <button
              className={cx(styles.btn, styles.btnDanger)}
              onClick={() => requestPanelAction("exclusion_pathway", "Reason for starting the Section 4.7 exclusion pathway:")}
              disabled={panelActionSaving}
            >
              Exclusion pathway, Section 4.7
            </button>
          </>
        ) : (
          <>
            <button
              className={cx(styles.btn, styles.btnPrimary)}
              onClick={() => requestPanelAction("acknowledge_close", "Reason for acknowledging and closing this item:")}
              disabled={panelActionSaving}
            >
              Acknowledge and close
            </button>
            <button
              className={styles.btn}
              onClick={() => requestPanelAction("add_note", "Note to add to the beneficiary record:")}
              disabled={panelActionSaving}
            >
              Add a note to the record
            </button>
            <button className={styles.btn} onClick={toggleFullHistory} disabled={historyLoading}>
              {showFullHistory ? "Hide full history" : "Open the full history"}
            </button>
          </>
        )}
        <button className={styles.btn} onClick={() => onAuditExport(false)} disabled={auditExporting}>
          {auditExporting ? "Exporting…" : "Export audit file"}
        </button>
        <button className={styles.btn} onClick={() => onAuditExport(true)} disabled={auditExporting}>
          Certify and export for CMS
        </button>
      </div>

      <div
        style={{ fontSize: ".85rem", color: "var(--gray500)", marginTop: ".8rem" }}
      >
        Every action here writes to the escalation record with your name,
        license and the reason. Nothing closes without one.
      </div>
    </>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────
function Row({ p, open, loadingDetail, detail, onToggle, onPainDecision, painDecisionSaving, onPanelAction, panelActionSaving, onAuditExport, auditExporting }) {
  const sev = severityOf(p);
  const trend = painTrend(p);
  const monthLabel = p.monthStatus || p.month || null;

  return (
    <>
      <div
        className={cx(styles.brow, sev && styles.browExc, open && styles.browOpen)}
        onClick={onToggle}
      >
        <div className={styles.caret}>▶</div>
        <div className={styles.name}>
          {dash(p.legalFirstName)} {dash(p.legalLastName)}
          <span>{dash(p.beneficiaryId)}</span>
        </div>
        <div>{dash(p.state)}</div>
        <div className={styles.hideSm}>{dash(p.dayOfPeriod)}</div>
        <div
          className={cx(
            styles.hideSm,
            trend?.dir && styles.trend,
            trend?.dir === "up" ? styles.trendUp : trend?.dir === "down" ? styles.trendDown : ""
          )}
        >
          {trend ? trend.label : "—"}
        </div>
        <div className={styles.hideSm}>{dash(p.next_reporting_window_open)}</div>
        <div>
          {p.latestFlagType ? (
            <span className={cx(styles.tag, sev === "stop" ? styles.tagStop : styles.tagWarn)}>
              {p.latestFlagType}
            </span>
          ) : p.latestFlagStatus ? (
            <span className={cx(styles.tag, styles.tagGrey)}>{p.latestFlagStatus}</span>
          ) : (
            <span className={cx(styles.tag, styles.tagGrey)}>None</span>
          )}
        </div>
        <div className={styles.hideSm}>
          {monthLabel === "Ready" ? (
            <span className={styles.tag}>Ready</span>
          ) : monthLabel ? (
            <span className={cx(styles.tag, styles.tagStop)}>{monthLabel}</span>
          ) : (
            <span className={cx(styles.tag, styles.tagGrey)}>—</span>
          )}
        </div>
      </div>
      <div className={cx(styles.detail, open && styles.detailOn)}>
        {open &&
          (loadingDetail ? (
            <div className={styles.dcard}>Loading record…</div>
          ) : (
            <Detail
              p={p}
              d={detail}
              onPainDecision={onPainDecision}
              painDecisionSaving={painDecisionSaving}
              onPanelAction={onPanelAction}
              panelActionSaving={panelActionSaving}
              onAuditExport={onAuditExport}
              auditExporting={auditExporting}
            />
          ))}
      </div>
    </>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────
export default function ReportPanel(props) {
  const [view, setView] = useState("panel");
  const [filter, setFilter] = useState("exc");
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [openRows, setOpenRows] = useState(new Set());
  const [attested, setAttested] = useState(false);

  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [loadingDetailRef, setLoadingDetailRef] = useState(null);
  const [monthEnd, setMonthEnd] = useState(null);

  const [loadingPeople, setLoadingPeople] = useState(true);
  const [loadingMonthEnd, setLoadingMonthEnd] = useState(true);

  const [toast, setToast] = useState(null); // { type: "info" | "success" | "error", text }
  const [approving, setApproving] = useState(false);
  const [painDecisionSavingRef, setPainDecisionSavingRef] = useState(null);
  const [panelActionSavingRef, setPanelActionSavingRef] = useState(null);
  const [auditExportingRef, setAuditExportingRef] = useState(null);
  const [panelAuditExporting, setPanelAuditExporting] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchSummary = useCallback(async () => {
    authHeader();
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/summary`);
      if (resp.data.success) setSummary(resp.data.data);
    } catch (e) {
      // summary stays null — the strip below falls back to "—" per stat
    }
  }, []);

  const fetchBeneficiaries = useCallback(async () => {
    authHeader();
    setLoadingPeople(true);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiaries`, {
        params: { filter, state: stateFilter, search },
      });
      if (resp.data.success) setPeople(resp.data.data || []);
      else setPeople([]);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.data?.responseCode === 401) {
        props.checkLoggedIn(false);
        return;
      }
      setPeople([]);
    } finally {
      setLoadingPeople(false);
    }
  }, [filter, stateFilter, search, props.checkLoggedIn]);

  const fetchMonthEnd = useCallback(async () => {
    authHeader();
    setLoadingMonthEnd(true);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/month-end`);
      if (resp.data.success) setMonthEnd(resp.data.data);
    } catch (e) {
      // monthEnd stays null — the tab still renders with empty defaults
    } finally {
      setLoadingMonthEnd(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchMonthEnd();
  }, [fetchSummary, fetchMonthEnd]);

  useEffect(() => {
    fetchBeneficiaries();
  }, [fetchBeneficiaries]);

  const toggleRow = async (ref) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });

    if (!detailCache[ref]) {
      authHeader();
      setLoadingDetailRef(ref);
      try {
        const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}`);
        if (resp.data.success) {
          setDetailCache((prev) => ({ ...prev, [ref]: resp.data.data }));
        }
      } catch (e) {
        // leave this ref out of the cache; Detail renders its empty state
      } finally {
        setLoadingDetailRef((cur) => (cur === ref ? null : cur));
      }
    }
  };

  const submitPainDecision = async (p, decision, revisitDate = null) => {
    const ref = p.beneficiaryId;
    const escalationId = detailCache[ref]?.painEscalation?.id ?? p.painEscalationId;
    if (!escalationId) {
      setToast({ type: "error", text: "No open second pain increase was found for this beneficiary." });
      return;
    }
    console.log("revisitDate", revisitDate);
    if (decision === "resume_changed_plan" && (!revisitDate || revisitDate === null)) {
      setToast({ type: "error", text: "Please provide a revisit date for the modified plan." });
      return;
    }

    authHeader();
    setPainDecisionSavingRef(ref);
    try {
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}/pain-escalation/decision`, {
        escalationId,
        decision,
        ...(decision === "keep_holding" || decision === "resume_changed_plan" ? { revisitDate } : {}),
      });
      if (!resp.data.success) {
        setToast({ type: "error", text: resp.data.message || "Failed to record the therapist decision." });
        return;
      }

      const successText = decision === "resume_changed_plan"
        ? "Decision recorded. Care can resume with the modified plan."
        : decision === "keep_holding"
          ? `Decision recorded. Hold remains active until review on ${revisitDate}.`
          : "Decision recorded. In-person referral is an escalation; the beneficiary remains enrolled.";
      setToast({ type: "success", text: successText });

      setDetailCache((prev) => {
        const next = { ...prev };
        delete next[ref];
        return next;
      });
      try {
        const detailResp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}`);
        if (detailResp.data.success) {
          setDetailCache((prev) => ({ ...prev, [ref]: detailResp.data.data }));
        }
      } catch (e) {
        // The decision is already recorded; a refresh failure must not report it as failed.
      }
      await Promise.all([fetchSummary(), fetchBeneficiaries()]);
    } catch (err) {
      setToast({ type: "error", text: err.response?.data?.message || "Failed to record the therapist decision." });
    } finally {
      setPainDecisionSavingRef((cur) => (cur === ref ? null : cur));
    }
  };

  const submitPanelAction = async (p, action, reason) => {
    const ref = p.beneficiaryId;
    authHeader();
    setPanelActionSavingRef(ref);
    try {
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}/action`, {
        action,
        reason,
        flagType: p.latestFlagType || null,
        flagDate: p.latestFlagDate || null,
      });
      if (!resp.data.success) {
        setToast({ type: "error", text: resp.data.message || "Failed to record the panel action." });
        return false;
      }

      setToast({
        type: "success",
        text: action === "add_note" ? "Note added to the beneficiary record." : "Action recorded in the beneficiary history.",
      });
      setDetailCache((prev) => {
        const next = { ...prev };
        delete next[ref];
        return next;
      });
      try {
        const detailResp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}`);
        if (detailResp.data.success) {
          setDetailCache((prev) => ({ ...prev, [ref]: detailResp.data.data }));
        }
      } catch (e) {
        // The action is already recorded; a detail refresh failure must not report it as failed.
      }
      await Promise.all([fetchSummary(), fetchBeneficiaries()]);
      return true;
    } catch (err) {
      setToast({ type: "error", text: err.response?.data?.message || "Failed to record the panel action." });
      return false;
    } finally {
      setPanelActionSavingRef((cur) => (cur === ref ? null : cur));
    }
  };

  const openMonthEndRecord = async (ref) => {
    setView("panel");
    setFilter("all");
    setStateFilter("");
    setSearch(String(ref));
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.add(ref);
      return next;
    });

    if (!detailCache[ref]) {
      authHeader();
      setLoadingDetailRef(ref);
      try {
        const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}`);
        if (resp.data.success) {
          setDetailCache((prev) => ({ ...prev, [ref]: resp.data.data }));
        }
      } catch (err) {
        setToast({ type: "error", text: err.response?.data?.message || "Failed to open the beneficiary record." });
      } finally {
        setLoadingDetailRef((cur) => (cur === ref ? null : cur));
      }
    }
  };

  const saveAuditDownload = (resp, fallbackName) => {
    const disposition = resp.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = match?.[1] || fallbackName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportBeneficiaryAudit = async (p, certify = false) => {
    const ref = p.beneficiaryId;
    authHeader();
    setAuditExportingRef(ref);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/beneficiary/${ref}/audit-export`, {
        params: certify ? { certify: 1 } : {},
        responseType: "blob",
      });
      saveAuditDownload(resp, `access-audit-beneficiary-${ref}.html`);
      setToast({ type: "success", text: certify ? "Certified CMS audit file exported." : "Beneficiary audit file exported." });
    } catch (err) {
      const text = err.response?.status === 403 && certify
        ? "Your account is not configured as an authorized CMS audit certifier."
        : "Failed to export the beneficiary audit file.";
      setToast({ type: "error", text });
    } finally {
      setAuditExportingRef((cur) => (cur === ref ? null : cur));
    }
  };

  const exportPanelAudit = async (certify = false) => {
    const from = window.prompt("Audit start date (YYYY-MM-DD), or leave blank for all available history:", "");
    if (from === null) return;
    const to = window.prompt("Audit end date (YYYY-MM-DD), or leave blank through the present:", "");
    if (to === null) return;
    authHeader();
    setPanelAuditExporting(true);
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/panel_report/audit-export`, {
        params: { ...(from.trim() ? { from: from.trim() } : {}), ...(to.trim() ? { to: to.trim() } : {}), ...(certify ? { certify: 1 } : {}) },
        responseType: "blob",
      });
      saveAuditDownload(resp, `access-audit-panel-${new Date().toISOString().slice(0, 10)}.html`);
      setToast({ type: "success", text: certify ? "Certified CMS panel audit register exported." : "Panel audit register exported." });
    } catch (err) {
      const text = err.response?.status === 403 && certify
        ? "Your account is not configured as an authorized CMS audit certifier."
        : "Failed to export the panel audit register. Check the date format and try again.";
      setToast({ type: "error", text });
    } finally {
      setPanelAuditExporting(false);
    }
  };

  const exportMonthEndList = () => {
    const rows = monthEnd?.claims || [];
    if (!rows.length) {
      setToast({ type: "info", text: "There are no month-end claims to export." });
      return;
    }

    const columns = [
      ["Beneficiary ID", "patient_id"],
      ["First name", "legalFirstName"],
      ["Last name", "legalLastName"],
      ["Date of service", "date_of_service"],
      ["Qualifying event", "qualifying_event_type"],
      ["Claim status", "status"],
      ["Review status", "reviewStatus"],
      ["Attested at", "attested_at"],
    ];
    const csvCell = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      return /[,"\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const exportRows = rows.map((r) => ({
      ...r,
      reviewStatus: r.blockingReason || (r.attested_at ? "Attested" : r.status === "pending_submission" ? "Ready" : r.status),
    }));
    const csv = [
      columns.map(([label]) => csvCell(label)).join(","),
      ...exportRows.map((r) => columns.map(([, key]) => csvCell(r[key])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `panel-report-month-end-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setToast({ type: "success", text: `${rows.length} month-end claim${rows.length === 1 ? "" : "s"} exported.` });
  };

  const approve = async () => {
    if (!attested) {
      setToast({ type: "info", text: "Please tick the attestation before approving." });
      return;
    }
    if (readyCount <= 0) {
      setToast({ type: "info", text: "Nothing to approve — 0 claims are ready this month." });
      return;
    }
    authHeader();
    setApproving(true);
    try {
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/panel_report/month-end/approve`, {
        attestationText:
          "I have reviewed the care furnished to the beneficiaries on my panel and attest that active care delivery was furnished in ongoing compliance with the model requirements.",
      });
      if (resp.data.success) {
        setToast({ type: resp.data.approvedCount > 0 ? "success" : "info", text: resp.data.message });
        setAttested(false);
        fetchMonthEnd();
        fetchBeneficiaries();
      } else {
        setToast({ type: "error", text: resp.data.message || "Failed to approve batch." });
      }
    } catch (err) {
      setToast({ type: "error", text: "Failed to approve batch." });
    } finally {
      setApproving(false);
    }
  };

  const readyCount = monthEnd?.readyCount ?? 0;
  const blocked = monthEnd?.blocked ?? [];
  const period = monthEnd?.period ?? "This period";
  const totalBillable = monthEnd?.totalBillable ?? 0;
  const needsDecision = monthEnd?.needsDecision ?? blocked.length;
  const totalAmount = typeof monthEnd?.totalAmount === "number" ? monthEnd.totalAmount : 0;
  const cptAmount = monthEnd?.cptAmount ?? "—";
  const notBillable = monthEnd?.notBillable;

  return (
    <div className={styles.page}>
      <div className={styles.bar}>
        <div className={styles.barIn}>
          <div className={styles.logo}>
            <span className={styles.v}>virdio</span>
            <span className={styles.h}>health</span>
          </div>
          <div className={styles.who}>
            {dash(summary?.therapistName)} &nbsp;|&nbsp; Licensed{" "}
            {summary?.licensedStates?.length ? summary.licensedStates.join(", ") : "—"} &nbsp;|&nbsp; Panel
            of {summary?.onPanel ?? "—"}
          </div>
        </div>
      </div>

      <div className={styles.wrap}>
        <h1>Panel report</h1>
        <p className={styles.sub}>
          One report, two views. The panel carries every beneficiary with
          exceptions surfaced in place and expandable to the full record. The
          month-end review is the Section 4.03(E) attestation and the gate
          before claims go out.
        </p>

        <div className={styles.tabs}>
          <button
            className={cx(styles.tab, view === "panel" && styles.tabOn)}
            onClick={() => setView("panel")}
          >
            Panel
          </button>
          <button
            className={cx(styles.tab, view === "month" && styles.tabOn)}
            onClick={() => setView("month")}
          >
            Month-end review <span className={styles.cnt}>{needsDecision}</span>
          </button>
        </div>

        {/* ══════════════ PANEL ══════════════ */}
        <div className={cx(styles.view, view === "panel" && styles.viewOn)}>
          <div className={styles.strip}>
            <div className={styles.stat}>
              <div className={styles.n}>{summary?.onPanel ?? "—"}</div>
              <div className={styles.l}>On panel</div>
            </div>
            <div className={cx(styles.stat, styles.statAlert)}>
              <div className={styles.n}>{summary?.openExceptions ?? "—"}</div>
              <div className={styles.l}>Open exceptions</div>
            </div>
            <div className={cx(styles.stat, styles.statWarn)}>
              <div className={styles.n}>{summary?.measureDueIn14Days ?? "—"}</div>
              <div className={styles.l}>Measure due in 14 days</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.n}>{summary?.billableThisMonth ?? "—"}</div>
              <div className={styles.l}>Billable this month</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.n}>{summary?.paused ?? "—"}</div>
              <div className={styles.l}>Paused</div>
            </div>
          </div>

          <div className={styles.filters}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                className={cx(styles.chip, filter === f.key && styles.chipOn)}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              {STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <input
              type="search"
              placeholder="Search name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 180 }}
            />
            <button className={styles.btn} onClick={() => exportPanelAudit(false)} disabled={panelAuditExporting}>
              {panelAuditExporting ? "Exporting…" : "Export audit register"}
            </button>
            <button className={styles.btn} onClick={() => exportPanelAudit(true)} disabled={panelAuditExporting}>
              Certify and export CMS register
            </button>
          </div>

          <div className={styles.tbl}>
            <div className={styles.hrow}>
              <div></div>
              <div>Beneficiary</div>
              <div>State</div>
              <div className={styles.hideSm}>Day</div>
              <div className={styles.hideSm}>Pain</div>
              <div className={styles.hideSm}>Next measure</div>
              <div>Flag</div>
              <div className={styles.hideSm}>Month</div>
            </div>
            <div>
              {loadingPeople ? (
                <div className={styles.brow}>Loading panel…</div>
              ) : people.length ? (
                people.map((p) => (
                  <Row
                    key={p.beneficiaryId}
                    p={p}
                    open={openRows.has(p.beneficiaryId)}
                    loadingDetail={loadingDetailRef === p.beneficiaryId}
                    detail={detailCache[p.beneficiaryId]}
                    onToggle={() => toggleRow(p.beneficiaryId)}
                    onPainDecision={(decision, revisitDate) => submitPainDecision(p, decision, revisitDate)}
                    painDecisionSaving={painDecisionSavingRef === p.beneficiaryId}
                    onPanelAction={(action, reason) => submitPanelAction(p, action, reason)}
                    panelActionSaving={panelActionSavingRef === p.beneficiaryId}
                    onAuditExport={(certify) => exportBeneficiaryAudit(p, certify)}
                    auditExporting={auditExportingRef === p.beneficiaryId}
                  />
                ))
              ) : (
                <div className={styles.brow}>No beneficiaries match this view.</div>
              )}
            </div>
          </div>

          <h2>What is on this view and why</h2>
          <div className={styles.good}>
            Combining the panel and the exception queue means the therapist
            works one list rather than switching between two. Exceptions sort
            to the top and stay in place in the panel, so the context around a
            flagged beneficiary is one row away rather than one screen away.
            Expanding a row opens the full record required at Section 6.1, so
            there is no third screen either.
          </div>
          <div className={styles.note}>
            <b>Alerts route by state of licensure.</b> This therapist sees only
            beneficiaries in states where she holds a license. That is not a
            filter she sets, it is how the panel is built.
          </div>
        </div>

        {/* ══════════════ MONTH END ══════════════ */}
        <div className={cx(styles.view, view === "month" && styles.viewOn)}>
          <div className={styles.mhead}>
            <div>
              <div className={styles.big}>{period} review</div>
              <div className={styles.m}>
                {totalBillable} beneficiaries with care furnished this month.{" "}
                {needsDecision} need a decision before the batch can be approved.
                {notBillable != null && ` ${notBillable} had no care furnished and are not billable.`}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className={styles.big} style={{ color: "var(--teal-deep)" }}>
                ${totalAmount.toFixed(2)}
              </div>
              <div className={styles.m}>
                {readyCount} claims at ${cptAmount} billed to Medicare
              </div>
            </div>
          </div>

          <div className={styles.note}>
            <b>Only the beneficiaries below need individual attention.</b> The
            rest have care furnished, no open exception, no pause days and a
            complete record. They are approved as a batch by the single action
            at the bottom, and the attestation is recorded against every one of
            them individually.
          </div>

          <div className={styles.tbl}>
            <div className={cx(styles.mrow, styles.mrowHead)}>
              <div>Beneficiary</div>
              <div>Date of service</div>
              <div>What furnished care</div>
              <div>Why it needs you</div>
              <div>Decision</div>
            </div>

            {loadingMonthEnd ? (
              <div className={styles.mrow}>Loading month-end review…</div>
            ) : blocked.length ? (
              blocked.map((b) => (
                <div key={b.id} className={cx(styles.mrow, styles.mrowBlocked)}>
                  <div>
                    <strong>
                      {dash(b.legalFirstName)} {dash(b.legalLastName)}
                    </strong>
                    <br />
                    <span style={{ fontSize: ".82rem", color: "var(--gray500)" }}>
                      {dash(b.patient_id)}
                    </span>
                  </div>
                  <div>{dash(b.date_of_service)}</div>
                  <div>—</div>
                  <div style={{ color: "var(--red-text)", fontWeight: 600 }}>
                    {dash(b.blockingReason || "Needs review")}
                  </div>
                  <div>
                    <button
                      className={cx(styles.btn, styles.btnPrimary)}
                      onClick={() => openMonthEndRecord(b.patient_id)}
                    >
                      Open record
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.mrow}>Nothing is blocking this batch.</div>
            )}

            <div className={styles.mrow} style={{ background: "var(--gray50)" }}>
              <div>
                <strong>{readyCount} others</strong>
                <br />
                <span style={{ fontSize: ".82rem", color: "var(--gray500)" }}>
                  No open exception, no pause, complete record
                </span>
              </div>
              <div>Various</div>
              <div>Sessions and measures</div>
              <div style={{ color: "var(--gray500)" }}>Nothing outstanding</div>
              <div>
                <span className={styles.tag}>Ready</span>
              </div>
            </div>
          </div>

          <div className={styles.attest}>
            <label>
              <input
                type="checkbox"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
              />
              <span className={styles.t}>
                I have reviewed the care furnished to the beneficiaries on my
                panel for {period} and I attest that active care delivery was
                furnished and that care was delivered in ongoing compliance
                with the model requirements.
                <span
                  style={{
                    display: "block",
                    marginTop: ".5rem",
                    fontWeight: 400,
                    fontSize: ".9rem",
                    color: "var(--gray600)",
                  }}
                >
                  Recorded against each of the {readyCount} beneficiaries in
                  this batch, with my name, license and the timestamp. This is
                  the Section 4.03(E) attestation. There is no separate CMS
                  form. Approving releases the claims.
                </span>
              </span>
            </label>
            <div className={styles.acts}>
              <button
                className={cx(styles.btn, styles.btnPrimary)}
                onClick={approve}
                disabled={approving || readyCount <= 0}
                style={readyCount <= 0 ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              >
                {approving ? "Approving…" : `Approve ${readyCount} and release claims`}
              </button>
              <button className={styles.btn} onClick={exportMonthEndList}>Export the list</button>
            </div>
          </div>

          <h2>How the attestation is made, and how much work it is</h2>
          <div className={styles.good}>
            <b>CMS requires no per-beneficiary sign-off.</b> Section 4.03(E)
            makes the attestation of active care delivery and ongoing
            compliance through claim submission itself. There is no form, no
            signature per patient and no named reviewer per patient. The
            monthly review is our internal control, so we get to shape it.
          </div>
          <div className={styles.note}>
            <b>So the volume is one action, not {totalBillable || "many"}.</b>{" "}
            Beneficiaries with a complete record and no open exception clear as
            a batch. The only ones pulled out are those where something is
            genuinely unresolved: an open exception, a mid-month pause, or a
            month whose only qualifying event is ambiguous. At panel scale
            that is a handful, not a panel.
          </div>
          <div className={styles.note}>
            <b>What blocks a claim.</b> No qualifying event in the calendar
            month. An open level B exception with no recorded decision. A
            pause covering the whole month. Everything else releases.
          </div>
        </div>
      </div>
      {toast && (
        <div
          role="status"
          style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            padding: "12px 20px", borderRadius: 8, color: "#fff", fontSize: ".92rem",
            zIndex: 1000, boxShadow: "0 4px 16px rgba(0,0,0,.2)",
            background: toast.type === "success" ? "#1a7f4e" : toast.type === "error" ? "#c62828" : "#333",
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
